import { Button, Dropdown, Icon, Popup } from "semantic-ui-react";

import { Checkbox } from "@/components/ui/checkbox";
import TagList from "components/TagList";
import { SELECT_COLUMN_ID } from "constants/Constants";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import CustomDropdown from "components/CustomDropdown";
import RuvixxDate from "components/RuvixxDate";
import { trimText } from "helpers/string";
import EntityService from "services/Entity";
import { NoticesTableActions } from "routes/campaign/components/notices_table/NoticesTableActions";
import { NOTICES_TABLE_CONSTANTS } from "routes/campaign/components/notices_table/NoticesTableV3";

const parseResponses = (responses = [], contact) => {
  const parsedResponses = responses.flatMap(formResponse => {
    const typeformEvents = (formResponse.typeform_events || []).map(event => ({
      ...event,
      language: formResponse.language,
      contact,
    }));

    const customFormEvents = (formResponse.custom_form_events || []).map(
      event => ({
        ...event,
        id: formResponse.id,
        language: formResponse.language,
        contact,
      })
    );

    return [...typeformEvents, ...customFormEvents];
  });
  return parsedResponses;
};

export const getNoticesTableColumns = ({
  entityId = null,
  handleOpenResponsesModal,
  invalidateData,
}) => {
  return [
    {
      id: SELECT_COLUMN_ID,
      size: 60,
      enableSorting: false,
      meta: {
        isDisplay: true,
      },
      header: ({ table }) => (
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onCheckedChange: checked => table.toggleAllRowsSelected(!!checked),
            "aria-label": "Select all rows",
          }}
        />
      ),
      cell: ({ row }) => (
        <div>
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected() && !row.getIsSelected(),
              onCheckedChange: checked => row.toggleSelected(!!checked),
              "aria-label": "Select row",
            }}
          />
        </div>
      ),
    },
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
      cell: ({ row }) => (
        <p style={{ fontWeight: "bold" }}>{row.original.id}</p>
      ),
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.original.tags}
          modelType={NOTICES_TABLE_CONSTANTS.className}
          modelId={row.original.id}
          tableCell
          onUpdate={invalidateData}
        />
      ),
    },
    ...(!entityId
      ? [
          {
            header: "Entity",
            id: "Entity",
            accessorKey: "entity",
            cell: ({ row }) => (
              <TableLink href={`/entities/${row.original.entity_id}`}>
                {row.original.entity}
              </TableLink>
            ),
          },
          {
            header: "Account Manager",
            id: "Account Manager",
            accessorKey: "account_managers",
            enableSorting: false,
            cell: ({ row }) => (
              <ScrollColumn>
                {row.original.account_managers.map(
                  ({ user_id, user_full_name }) => (
                    <li key={user_id}>{user_full_name}</li>
                  )
                )}
              </ScrollColumn>
            ),
          },
        ]
      : []),
    {
      header: "Recipient ID",
      id: "Recipient ID",
      accessorKey: "contact_id",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.original.contact_id}`}>
          {row.original.contact_id}
        </TableLink>
      ),
    },
    {
      header: "Recipient",
      id: "Recipient",
      accessorKey: "contact_name",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.original.contact_id}`}>
          {row.original.contact_name}
        </TableLink>
      ),
    },
    {
      header: "Email",
      id: "Email",
      accessorKey: "email",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.original.contact_id}`}>
          {row.original.email}
        </TableLink>
      ),
    },
    {
      header: "From Address",
      id: "From Address",
      accessorKey: "from_address",
    },
    {
      header: "Sent By",
      id: "Sent By",
      accessorKey: "creator_user_full_name",
    },
    {
      header: "CC",
      id: "CC",
      accessorKey: "info.cc",
      enableSorting: false,
    },
    {
      header: "BCC",
      id: "BCC",
      accessorKey: "info.bcc",
      enableSorting: false,
    },
    {
      header: "Template",
      id: "Template",
      accessorKey: "template_name",
    },
    {
      header: "Delivered",
      id: "Delivered",
      accessorKey: "metrics.first_delivered_at",
      cell: ({ row }) => {
        const value = row?.original?.metrics?.first_delivered_at;
        if (value !== undefined && value !== null) {
          return <RuvixxDate date={value} />;
        }
        return null;
      },
    },
    {
      header: "First Opened",
      id: "First Opened",
      accessorKey: "metrics.first_opened_at",
      cell: ({ row }) => {
        const value = row?.original?.metrics?.first_opened_at;
        if (value !== undefined && value !== null) {
          return <RuvixxDate date={value} />;
        }
        return null;
      },
    },
    {
      header: "Last Opened",
      id: "Last Opened",
      accessorKey: "metrics.last_opened_at",
      cell: ({ row }) => {
        const value = row?.original?.metrics?.last_opened_at;
        if (value !== undefined && value !== null) {
          return <RuvixxDate date={value} />;
        }
        return null;
      },
    },
    {
      header: "Manual email",
      id: "Manual email",
      accessorKey: "is_manual",
      cell: ({ row }) => {
        return (
          <div style={{ textAlign: "center", minWidth: "25px" }}>
            <Icon name={row.original?.is_manual ? "check" : null} />
          </div>
        );
      },
    },
    {
      header: "Attachments",
      id: "Attachments",
      accessorKey: "attachments",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original?.attachments || [];
        if (value && value.length > 0) {
          return (
            <CustomDropdown icon="attach">
              <Dropdown.Menu direction="left">
                {value.map(attachment => (
                  <Dropdown.Item
                    content={attachment.file_name}
                    onClick={() => window.open(attachment.url)}
                    key={attachment.url}
                  />
                ))}
              </Dropdown.Menu>
            </CustomDropdown>
          );
        } else {
          return "--";
        }
      },
    },
    {
      header: "Responses",
      id: "Responses",
      accessorKey: "responses",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original?.responses || [];
        if (value.length) {
          const contact = {
            id: row.original.contact_id,
            email: row.original.contact_email,
            full_name: row.original.contact_name,
          };
          const responses = parseResponses(value, contact);
          return (
            <div>
              <Button
                basic
                size="tiny"
                content="Show Responses"
                onClick={() => {
                  handleOpenResponsesModal(
                    responses,
                    value.map(({ id }) => id)
                  );
                }}
              />
            </div>
          );
        } else {
          return "--";
        }
      },
    },
    {
      header: "Failed At",
      id: "Failed At",
      accessorKey: "metrics.last_failed_at",
      cell: ({ row }) => {
        const value = row.original?.metrics?.last_failed_at;
        const lastErroredAt = row.original?.metrics?.last_errored_at;
        if (lastErroredAt) {
          return <RuvixxDate date={lastErroredAt} />;
        } else if (value) {
          return <RuvixxDate date={value} />;
        }
        return null;
      },
    },
    {
      header: "Error Message",
      id: "Error Message",
      accessorKey: "metrics.error_message",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original?.metrics?.error_message;
        if (value) {
          return (
            <Popup
              position="top right"
              trigger={<div>{trimText(value, 15)}</div>}
              content={<div>{value}</div>}
            />
          );
        }
        return "--";
      },
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableResizing: false,
      size: 60,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => <NoticesTableActions original={row.original} />,
    },
  ];
};

export const getNoticesTableFilters = ({
  tags,
  users,
  templates,
  entityId,
}) => {
  return [
    {
      key: "account_manager_id",
      title: "Account Manager",
      type: "select",
      data: users,
    },
    {
      key: "template_id",
      title: "Template",
      type: "select",
      data: templates,
    },
    {
      title: "Is Manual",
      key: "grid_1",
      type: "grid",
      components: [
        { key: "manual", title: "Manual" },
        { key: "not_manual", title: "Not Manual" },
      ],
    },
    {
      title: "From Address",
      key: "from_address",
      type: "input",
    },
    {
      key: "status",
      title: "Status",
      type: "select",
      data: [
        { id: "delivered", name: "Delivered" },
        { id: "opened", name: "Opened" },
        { id: "failed", name: "Failed" },
      ],
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    ...(!entityId
      ? [
          {
            key: "assigned_user_id",
            title: "Assignee",
            type: "select",
            data: users,
          },
          {
            key: "entity_id",
            title: "Entity",
            type: "ruvixxSelect",
            queryFn: EntityService.getEntitiesForFilters,
          },
        ]
      : []),
  ];
};
